import { z } from "zod/v3";
import {
  CANONICAL_COMPLIANCE_ROLES,
  type CanonicalRoleKey,
} from "./compliance-role.dto";
import { PersonGroupSystemKeySchema } from "./person-group.dto";

const CANONICAL_ROLE_KEYS = Object.keys(CANONICAL_COMPLIANCE_ROLES) as [
  CanonicalRoleKey,
  ...CanonicalRoleKey[],
];

export const RunbookTransferAssignmentSchema = z
  .discriminatedUnion("type", [
    z
      .object({
        type: z.literal("compliance_role"),
        roleKey: z.enum(CANONICAL_ROLE_KEYS),
      })
      .strict(),
    z.object({ type: z.literal("subject_manager") }).strict(),
    z
      .object({
        type: z.literal("system_group"),
        systemKey: PersonGroupSystemKeySchema,
      })
      .strict(),
    z
      .object({
        type: z.literal("parameter_manager"),
        source: z
          .string()
          .trim()
          .regex(/^@params\.[A-Za-z0-9_]+\.manager$/),
      })
      .strict(),
    z
      .object({
        type: z.literal("unresolved"),
        label: z.string().min(1),
      })
      .strict(),
  ])
  .nullable();

export type RunbookTransferAssignment = z.infer<
  typeof RunbookTransferAssignmentSchema
>;

const RUNBOOK_INTERNAL_ASSIGNMENT_KEYS = [
  "assignedRole",
  "assignedRoleId",
  "assignedUserId",
  "assignedGroup",
  "assignedFrom",
  "assignmentSelector",
] as const;

export const RunbookTransferStepSchema = z
  .object({ assignment: RunbookTransferAssignmentSchema })
  .passthrough()
  .superRefine((step, ctx) => {
    for (const key of RUNBOOK_INTERNAL_ASSIGNMENT_KEYS) {
      if (Object.prototype.hasOwnProperty.call(step, key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is internal and cannot appear in a procedure transfer`,
        });
      }
    }
  });

export type RunbookTransferStep = z.infer<typeof RunbookTransferStepSchema>;
